import { ActionTable, PermissionCheck } from '@/components';
import { useATSelection } from '@/components/table/hooks';
import { useAppSelector } from '@/state';
import { ProductionApi } from '@/state/api';
import { ProductionType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import {
  Box,
  Button,
  Table as ChakraTable,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  DataList,
  EmptyState,
  NumberInput,
  OverlayDialog,
  Table,
  toasterApi,
} from '@ui';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import z from 'zod';

type PackingSlipFramesProps = {
  selectedPackingSlip:
    | ProductionType.PackingSlip.GetPackingSlips['response'][number]
    | null;
};

const PackingSlipFrames_Inner = ({
  selectedPackingSlip,
}: PackingSlipFramesProps) => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );

  // Edited frame quantity before adding as selected frame and quantity [frame_id -> changed quantity]
  const [editedFrameQuantities, setEditedFrameQuantities] = useState<
    Map<number, number>
  >(() => new Map());
  // Selected Frames and quantity
  const { selection } = useATSelection();
  const selectedPackingSlipFramesAndQuantity: ProductionType.PackingSlip.GetPackingSlipFrames['response']['items'] =
    selection.getArray();

  // Delete from packing list
  const [deletePackingSlipFramesMutation] =
    ProductionApi.Api.useDeletePackingSlipFramesMutation();

  const {
    data: getPackingSlipFramesQuery,
    isLoading: isPackingSlipFramesQueryLoading,
  } = ProductionApi.Api.useGetPackingSlipFramesQuery(
    selectedPackingSlip?.id
      ? {
          params: {
            packing_slip_id: selectedPackingSlip?.id,
          },
        }
      : skipToken,
  );

  const handleDeleteFromPackingSlip = () => {
    if (!selectedPackingSlipFramesAndQuantity?.length) return;

    const payload: ProductionType.PackingSlip.DeletePackingSlipFrames['body'] =
      selectedPackingSlipFramesAndQuantity
        ?.filter((row) => row?.allowed_to_delete > 0)
        ?.map((row) => {
          return {
            item_id: row?.id,
            quantity: editedFrameQuantities?.get(row?.frame_id) ?? 1,
          };
        });

    if (!payload?.length) return;

    OverlayDialog.open('deletePacking', {
      overlayId: 'deletePacking',
      title: 'Packing',
      description:
        'Are you sure you want to delete the frames from the packing list?',
      content: (
        <VStack align="flex-start" gap={2}>
          <Text color="tertiary.fg">Frame Orders:</Text>
          <DataList
            items={[
              {
                label: 'Total Frames to delete from Packing Slip',
                value: `${selectedPackingSlipFramesAndQuantity?.length}`,
              },
              {
                label: 'Packing Slip',
                value: `${selectedPackingSlip?.name}`,
              },
              {
                label: 'Project Name',
                value: `${selectedProject?.item?.name}`,
              },
              {
                label: 'Project Location',
                value: `${selectedPackingSlip?.project_location}`,
              },
            ]}
            orientation="horizontal"
            divideY="1px"
          />
          <Table
            columns={[
              {
                frameName: {
                  label: 'Frame',
                  width: '200px',
                },
              },
              {
                quantity: {
                  label: 'Quantity',
                  width: '100px',
                },
              },
            ]}
            items={payload?.map((item) => ({
              frameName: selectedPackingSlipFramesAndQuantity?.find(
                (frame) => frame?.id === item?.item_id,
              )?.frame_name,
              quantity: item?.quantity,
            }))}
          />
        </VStack>
      ),
      handleConfirm: async () => {
        if (!selectedPackingSlip?.id) return;

        await deletePackingSlipFramesMutation({
          body: payload,
          params: {
            packing_slip_id: selectedPackingSlip?.id,
          },
        })
          .then(() => {
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Frames deleted from packing list successfully',
            });

            // Reset all states
            setEditedFrameQuantities(new Map());
            selection.clear();
          })
          .catch((e) => {
            toasterApi({
              type: 'error',
              status: 400,
              code: 'Error',
              message: e?.data?.detail,
            });
          })
          .finally(() => {
            // Reset all states
            OverlayDialog.close('confirmPackingList');
          });
      },
    });
  };

  // Reset edited quantities if frames list changes on invalidation
  useEffect(() => {
    setEditedFrameQuantities(new Map());
  }, [getPackingSlipFramesQuery]);

  const renderCell = (
    props: ActionTable.RenderCellType<PackingSlipFramesTableFramesType>,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    switch (colKey) {
      case 'quantity': {
        const item = rowValue;

        return (
          <Box width="100px" gapX={2} onClick={(e) => e.stopPropagation()}>
            <NumberInput
              defaultValue={1}
              min={1}
              max={item?.allowed_to_delete}
              value={
                item?.frame_id ? editedFrameQuantities?.get(item?.frame_id) : 1
              }
              onChange={(value: number) => {
                if (!getPackingSlipFramesQuery?.items?.length) return;

                // Update edited frame quantity before adding as selected frame and quantity
                setEditedFrameQuantities((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(item?.frame_id as number, value);
                  return newMap;
                });
              }}
            />
            <Text fontSize="xs" color="fg.warning">
              Available: {item?.allowed_to_delete}
            </Text>
          </Box>
        );
      }
      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <Flex flexDirection="column" gapY={4} width="full">
      <Flex width="full" flexDirection="row" bg="white" color="black">
        <Flex
          justifyContent="space-between"
          width="full"
          alignItems="center"
          padding={4}
        >
          <Heading size="5xl" textTransform="uppercase">
            Packing
            <br />
            Slip
          </Heading>
          <Image
            src="/logo-karas.png"
            alt="Karas Logo"
            width="200px"
            height="100px"
            objectFit="contain"
          />
        </Flex>
      </Flex>

      {!selectedPackingSlip?.id ? (
        <EmptyState
          isEmpty
          position="relative"
          title="Select a packing slip"
          description="Please select a packing slip to view the items"
        />
      ) : (
        <Flex flexDirection="column" gapY={4}>
          <ChakraTable.Root size="sm" stickyHeader bg="red">
            <ChakraTable.Header>
              <ChakraTable.Row bg="bg.subtle">
                <ChakraTable.ColumnHeader>
                  Date: {dayjs().format('DD MMMM YYYY')}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>
                  Project Name :
                  {getPackingSlipFramesQuery?.project_name ?? 'N/A'}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>
                  Project #: {getPackingSlipFramesQuery?.project_id ?? 'N/A'}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader />
              </ChakraTable.Row>
              <ChakraTable.Row>
                <ChakraTable.ColumnHeader>
                  Project Location :{' '}
                  {getPackingSlipFramesQuery?.project_location ?? 'N/A'}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>
                  Release #: {selectedRelease?.item?.number ?? 'N/A'}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader />
                <ChakraTable.ColumnHeader />
              </ChakraTable.Row>
              <ChakraTable.Row>
                <ChakraTable.ColumnHeader>Part #</ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>Description</ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>
                  Quantity: {getPackingSlipFramesQuery?.items?.length ?? 0}
                </ChakraTable.ColumnHeader>
                <ChakraTable.ColumnHeader>Crate #</ChakraTable.ColumnHeader>
              </ChakraTable.Row>
            </ChakraTable.Header>
          </ChakraTable.Root>

          <Flex flexDirection="column" gapY={4}>
            <Flex flexDirection="column" gapY={2}>
              <>
                <ActionTable.FilterBar
                  disabled={!selectedPackingSlip?.id}
                  filterConfigComponents={filterConfigComponents}
                />
                <Box
                  key={`packing-slip-frames-table-${selectedProject?.id}`}
                  display="flex"
                  flexDirection="row"
                  gap={5}
                >
                  <Box h="100%" my={5}>
                    <ActionTable<PackingSlipFramesTableFramesType>
                      width="50vw"
                      columns={columns}
                      columnToUseAsRowKey="frame_id"
                      items={getPackingSlipFramesQuery?.items ?? []}
                      isPaginated={false}
                      isPaginatedAsync={false}
                      totalItemsCount={
                        getPackingSlipFramesQuery?.items?.length ?? 0
                      }
                      isSortable={true}
                      isFilterable={true}
                      isShowActionBar={true}
                      height={'55vh'}
                      rowHeight={100}
                      renderCell={renderCell}
                      isLoading={
                        selectedProject.id
                          ? isPackingSlipFramesQueryLoading
                          : false
                      }
                      isMultipleSelection={true}
                      isCheckbox
                      isRowSelectionDisabled={(row) =>
                        row?.allowed_to_delete <= 0
                      }
                    />
                  </Box>
                </Box>
              </>
            </Flex>
            <Flex justifyContent="space-between">
              <PermissionCheck
                permissions={[permissionsKeys.delete_packingslip]}
              >
                <Button
                  bg="red.700"
                  onClick={handleDeleteFromPackingSlip}
                  disabled={!selectedPackingSlipFramesAndQuantity?.length}
                >
                  Delete from Packing
                </Button>
              </PermissionCheck>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const PackingSlipFrames = ({ selectedPackingSlip }: PackingSlipFramesProps) => {
  return (
    <ActionTable.Provider>
      <PackingSlipFrames_Inner selectedPackingSlip={selectedPackingSlip} />
    </ActionTable.Provider>
  );
};
export default PackingSlipFrames;

const packingSlipFrameItemsSchema =
  ProductionType.PackingSlip.getPackingSlipFrames.response.shape.items.element;

type PackingSlipFramesTableFramesType = z.infer<
  typeof packingSlipFrameItemsSchema
>;

const columns: ActionTable.ColumnsConfigType<PackingSlipFramesTableFramesType> =
  [
    {
      frame_name: {
        label: 'Frame',
        width: '150px',
      },
    },
    {
      quantity: {
        label: 'Remaining Quantity',
        width: '200px',
      },
    },
    {
      release_number: {
        label: 'Release Number',
        width: '150px',
      },
    },
  ];

const filterConfigComponents: ActionTable.FilterConfigComponentType<PackingSlipFramesTableFramesType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'Frame',
      filterConfigItems: [
        {
          colKeys: ['frame_name'],
          value: '',
          operator: 'contains',
        },
      ],
    },
    {
      filterComponent: 'input',
      filterComponentLabel: 'Release',
      filterConfigItems: [
        {
          colKeys: ['release_number'],
          value: '',
          operator: 'contains',
        },
      ],
    },
  ];
